import { collection, deleteDoc, deleteField, doc, DocumentData, DocumentSnapshot, getDoc, getDocs, setDoc } from "firebase/firestore";
import { auth, db } from "../../firebase/FirebaseConfig";
import { ProjectNote, ProjectWritingRequest } from "./types";
import { nanoid } from "nanoid";
import { ProjectStreamCallbacks, StreamProjectResponse } from "./ProjectStream";

export async function FetchNotes(projectID: string): Promise<ProjectNote[]> {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error("User not logged in");

  const notesDocs = await getDocs(collection(db, `users/${currentUser.uid}/projects/${projectID}/notes`));

  const notes: ProjectNote[] = notesDocs.docs
    .map((doc) => {
      try {
        return noteFromDoc(doc);
      } catch (e) {
        return null;
      }
    })
    .filter((note) => note !== null) as ProjectNote[];
  return notes;
}

export async function FetchNote(projectID: string, noteID: string): Promise<ProjectNote> {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error("User not logged in");

  const noteDoc = await getDoc(doc(db, `users/${currentUser.uid}/projects/${projectID}/notes/${noteID}`));

  return noteFromDoc(noteDoc);
}

export async function CreateNewNote(projectID: string) {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error("User not logged in");

  const note: ProjectNote = {
    id: nanoid(),
    name: "Untitled Note",
    dateCreated: new Date(),
    lastUpdated: new Date(),
    content: "",
  };
  const docRef = doc(db, `users/${currentUser.uid}/projects/${projectID}/notes/${note.id}`);
  await setDoc(docRef, note, { merge: true });

  return note;
}

export async function SaveNoteName(projectID: string, noteID: string, name: string) {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error("User not logged in");

  const docRef = doc(db, `users/${currentUser.uid}/projects/${projectID}/notes/${noteID}`);
  await setDoc(
    docRef,
    {
      lastUpdated: new Date(),
      name: name,
    },
    { merge: true }
  );
}

export async function SaveNoteContent(projectID: string, noteID: string, content?: string) {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error("User not logged in");

  const docRef = doc(db, `users/${currentUser.uid}/projects/${projectID}/notes/${noteID}`);
  await setDoc(
    docRef,
    {
      lastUpdated: new Date(),
      content: content ?? deleteField(),
    },
    { merge: true }
  );
}

export async function DeleteNote(projectID: string, noteID: string) {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error("User not logged in");

  const docRef = doc(db, `users/${currentUser.uid}/projects/${projectID}/notes/${noteID}`);
  await deleteDoc(docRef);
}

const noteFromDoc = (doc: DocumentSnapshot<DocumentData>): ProjectNote => {
  const data = doc.data();
  if (!data) throw new Error(`No note with ID ${doc.id} found.`);
  return {
    id: doc.id,
    name: data.name,
    dateCreated: data.dateCreated.toDate(),
    lastUpdated: data.lastUpdated.toDate(),
    content: data.content,
  };
};

export async function StreamAIWriting(
  projectID: string,
  request: ProjectWritingRequest,
  callbacks: ProjectStreamCallbacks,
  signal: AbortSignal
) {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error("User not logged in");

  await StreamProjectResponse(`${process.env.REACT_APP_SERVER_URL}/projects/${projectID}/writing/stream`, request, callbacks, signal);
}
